import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Filter, Square, X, Check } from 'lucide-react';
import _ from 'lodash';

const FilterModal = ({ 
  isOpen, 
  onClose, 
  columnName, 
  data, 
  activeFilters,
  onApplyFilter,
  position,
  schema,
  style = {}
}) => {
  const [selectedValues, setSelectedValues] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const modalRef = useRef();
  const searchInputRef = useRef();

  const formatColumnName = (name) => {
    return name
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const uniqueValues = useMemo(() => {
    if (!data || !columnName) return [];
    
    // First, apply filters from other columns
    const filteredData = data.filter(row => {
      return Object.entries(activeFilters).every(([filterCol, allowedValues]) => {
        // Skip current column when applying filters
        if (filterCol === columnName) return true;
        
        const cell = row[filterCol];
        // For numeric columns compare sortValue; for others use displayValue
        if (cell && typeof cell.sortValue === 'number') {
          return allowedValues.includes(cell.sortValue);
        } else {
          const stringValue = (cell?.displayValue ?? cell) === null || 
                             (cell?.displayValue ?? cell) === undefined 
            ? 'N/A' 
            : String(cell?.displayValue ?? cell);
          return allowedValues.includes(stringValue);
        }
      });
    });
  
    // Now build a map with raw and display values
    const valueMap = {};
    filteredData.forEach(row => {
      const cell = row[columnName];
      // Handle both object-style cells and direct values
      const raw = cell?.sortValue;
      const display = cell?.displayValue ?? cell;
      
      // Use raw numeric value as key if available, otherwise use string value
      const key = typeof raw === 'number' ? raw : 
                  (display === null || display === undefined ? 'N/A' : String(display));
      
      if (!valueMap[key]) {
        valueMap[key] = {
          raw: raw, 
          display: display === null || display === undefined ? 'N/A' : String(display),
          count: 0
        };
      }
      valueMap[key].count++;
    });
  
    // Return array of objects with raw value, display value, and count
    return Object.values(valueMap)
      .sort((a, b) => {
        // Sort by raw value if numeric, otherwise by display value
        if (typeof a.raw === 'number' && typeof b.raw === 'number') {
          return a.raw - b.raw;
        }
        return a.display.localeCompare(b.display);
      });
  }, [data, columnName, activeFilters]);

  // Initialize selected values from active filters
  useEffect(() => {
    if (!isOpen) return; // Only update when modal opens
    
    if (activeFilters && activeFilters[columnName]) {
      // Use raw values directly from activeFilters
      setSelectedValues(new Set(activeFilters[columnName]));
    } else {
      // Set all raw values selected by default
      setSelectedValues(new Set(uniqueValues.map(item => 
        // Use the raw value if it's a number, otherwise use the display value
        typeof item.raw === 'number' ? item.raw : item.display
      )));
    }
  }, [isOpen, columnName, uniqueValues, activeFilters]);

  // Handle Escape key
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.keyCode === 27) onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);

  // Focus on search input when modal opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleClickOutside = (event) => {
    if (isOpen && modalRef.current && !modalRef.current.contains(event.target)) {
      onClose();
    }
  };

  const filteredValues = useMemo(() => {
    if (!searchTerm) return uniqueValues;
    return uniqueValues.filter(item => 
      item.display.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [uniqueValues, searchTerm]);
  
  const areAllSelected = selectedValues.size === uniqueValues.length;
  
  const handleToggleAll = () => {
    if (areAllSelected) {
      setSelectedValues(new Set());
    } else {
      setSelectedValues(new Set(uniqueValues.map(item => 
        typeof item.raw === 'number' ? item.raw : item.display
      )));
    }
  };

  const handleValueToggle = (value) => {
    const newSelected = new Set(selectedValues);
    if (newSelected.has(value)) {
      newSelected.delete(value);
    } else {
      newSelected.add(value);
    }
    setSelectedValues(newSelected);
  };

  const handleApply = () => {
    const selectedArray = Array.from(selectedValues);
    
    // If search term exists, filter to only what's currently visible
    if (searchTerm.trim() !== '') {
      // Get raw values of items that match the search
      const visibleRawValues = filteredValues.map(item => 
        typeof item.raw === 'number' ? item.raw : item.display
      );
      
      // Use the intersection of selected values and visible values
      const filteredSelections = selectedArray.filter(value => 
        visibleRawValues.includes(value)
      );
      
      // If all values are selected (meaning no filter), pass an empty array
      if (filteredSelections.length === uniqueValues.length) {
        onApplyFilter(columnName, []);
      } else {
        onApplyFilter(columnName, filteredSelections);
      }
    } else {
      // Original behavior when no search is applied
      if (selectedArray.length === uniqueValues.length) {
        onApplyFilter(columnName, []);
      } else {
        onApplyFilter(columnName, selectedArray);
      }
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 overflow-y-auto"
      onClick={handleClickOutside}
    >
      <div className="fixed inset-0 bg-black/20" />
      
      <div 
        ref={modalRef}
        className="relative mx-auto bg-white rounded-lg shadow-xl animate-popIn flex flex-col"
        style={{
          width: '300px',
          maxHeight: 'calc(100vh - 40px)',
          margin: '20px auto',
          ...style
        }}
      >
        {/* Header */}
        <div className="bg-gray-100 px-6 py-4 flex justify-between items-center border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">Filter: {formatColumnName(columnName)}</h2>
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-gray-800 transition-colors duration-150"
          >
            <X size={24} />
          </button>
        </div>

        <div className="px-6 py-4 flex-grow overflow-hidden flex flex-col min-h-0">
          {/* Search Input */}
          <input
            type="text"
            placeholder="Search values..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            ref={searchInputRef}
            className="w-full p-2 border border-gray-300 rounded mb-4 text-sm text-text placeholder-text-lighter focus:outline-none focus:ring-2 focus:ring-secondary focus:border-secondary transition-colors duration-200"
          />

          {/* Select/Deselect All Button */}
          <div className="mb-4 pb-3 border-b border-gray-200">
            <button
              onClick={handleToggleAll}
              className="flex items-center space-x-2 text-sm text-gray-700 hover:text-gray-900"
            >
              <div className={`p-1 rounded ${
                areAllSelected
                  ? 'text-blue-600 bg-blue-100'
                  : 'text-gray-400 bg-gray-200'
              }`}>
                {areAllSelected ? <Check size={14} /> : <Square size={14} />}
              </div>
              <span>
                {areAllSelected ? 'Deselect All' : 'Select All'}
              </span>
            </button>
          </div>

          {/* Values List */}
          <div className="overflow-y-auto flex-grow min-h-0">
          {filteredValues.map(({raw, display, count}) => (
            <div
              key={typeof raw === 'number' ? `num-${raw}` : display}
              className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer transition-colors duration-200"
              onClick={() => handleValueToggle(typeof raw === 'number' ? raw : display)}
            >
              <div className={`p-1 rounded ${
                selectedValues.has(typeof raw === 'number' ? raw : display)
                  ? 'text-blue-600 bg-blue-100'
                  : 'text-gray-400 bg-gray-200'
              }`}>
                {selectedValues.has(typeof raw === 'number' ? raw : display) ? <Check size={14} /> : <Square size={14} />}
              </div>
              <div className="flex flex-1 justify-between items-center ml-2">
                <span className="text-sm text-gray-700 truncate">{display}</span>
                <span className="text-xs text-gray-500 ml-2">{count}</span>
              </div>
            </div>
          ))}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-3 border-t border-gray-200 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2.5 text-sm text-gray-600 hover:text-gray-900 font-medium transition-colors duration-200"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            className="px-6 py-2.5 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors duration-200"
          >
            Apply Filter
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterModal;